import React, { useState, useRef, useEffect } from 'react';
import { colors } from '../utils/colors';
import type { AdminUser } from '../utils/types';
import logoDark from '../assets/logo-dark.svg';
import { login, verifyOtp, getAdminProfile, clearToken } from '../Api/auth';

interface Props {
  onLogin: (user: AdminUser) => void;
}

const EyeIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke={colors.textLight} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {open ? (
      <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
    ) : (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
      </>
    )}
  </svg>
);

const inputWrap = (focused: boolean, err: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center',
  border: `1.5px solid ${err ? colors.dangerBorder : focused ? colors.primary : colors.border}`,
  borderRadius: 12, background: '#fff', padding: '0 16px', gap: 10,
  transition: 'border-color 0.2s',
});

const STATIC_EMAIL    = 'admin@vavschool.com';
const STATIC_PASSWORD = 'Admin@123';
const STATIC_OTP      = '123456';
const STATIC_USER: import('../utils/types').AdminUser = {
  id: '4', name: 'Admin User', role: 'admin', email: STATIC_EMAIL,
};

const LoginScreen: React.FC<Props> = ({ onLogin }) => {
  const [step, setStep] = useState<'credentials' | 'otp'>('credentials');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passFocused, setPassFocused]   = useState(false);

  const [otp, setOtp]                   = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError]         = useState('');
  const [otpLoading, setOtpLoading]     = useState(false);
  const [resendCooldown, setCooldown]   = useState(0);
  const isStaticLogin = useRef(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleCredentials = async () => {
    if (!email || !password) { setError('Please fill in all fields.'); return; }
    setLoading(true); setError('');
    if (email.trim() === STATIC_EMAIL && password === STATIC_PASSWORD) {
      isStaticLogin.current = true;
      setStep('otp'); setCooldown(60); setLoading(false);
      setTimeout(() => otpRefs.current[0]?.focus(), 300);
      return;
    }
    isStaticLogin.current = false;
    try {
      await login({ email: email.trim(), password });
      setStep('otp'); setCooldown(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 300);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setOtp(['', '', '', '', '', '']); setOtpError(''); setCooldown(60);
    otpRefs.current[0]?.focus();
    try { await login({ email: email.trim(), password }); } catch { /* silent */ }
  };

  const handleOtpChange = (i: number, val: string) => {
    const d = val.replace(/\D/g, '').slice(-1);
    const next = [...otp]; next[i] = d; setOtp(next); setOtpError('');
    if (d && i < 5) otpRefs.current[i + 1]?.focus();
  };

  const handleOtpKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const p = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (p.length === 6) { setOtp(p.split('')); otpRefs.current[5]?.focus(); }
    e.preventDefault();
  };

  const handleVerify = async () => {
    const code = otp.join('');
    if (code.length < 6) { setOtpError('Enter the 6-digit OTP.'); return; }
    setOtpLoading(true); setOtpError('');
    if (isStaticLogin.current) {
      if (code === STATIC_OTP) { onLogin(STATIC_USER); return; }
      setOtpError('Invalid OTP. Try again.');
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
      setOtpLoading(false);
      return;
    }
    try {
      const res = await verifyOtp({ email: email.trim(), otp: code });
      if (res.role !== 'accountant') {
        clearToken();
        throw new Error('Access denied. Only accountant accounts are allowed to log in.');
      }
      const profile: AdminUser = res.teacher_id != null
        ? await getAdminProfile(res.teacher_id)
        : { id: 'accountant', name: res.name ?? email.trim(), role: 'accountant', email: email.trim() };
      onLogin(profile);
    } catch (err) {
      setOtpError(err instanceof Error ? err.message : 'Verification failed. Try again.');
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    }
    setOtpLoading(false);
  };

  const baseContainer: React.CSSProperties = {
    height: '100dvh', background: '#fff', display: 'flex',
    flexDirection: 'column', overflow: 'hidden',
    fontFamily: "'Inter', sans-serif",
  };

  if (step === 'credentials') return (
    <div style={baseContainer}>
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 72, paddingBottom: 32 }}>
          <img src={logoDark} alt="VAV" width={100} height={100} style={{ borderRadius: 20 }} />
          <div style={{ fontSize: 26, fontWeight: 900, color: colors.primaryDark, marginTop: 14, letterSpacing: '-0.5px' }}>
            V.A.V School
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: colors.primary, letterSpacing: '2.5px', marginTop: 4 }}>
            ATTENDANCE PORTAL
          </div>
        </div>

        <div style={{ padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, letterSpacing: '0.8px', marginBottom: 8 }}>
              REGISTERED EMAIL
            </div>
            <div style={inputWrap(emailFocused, !!error)}>
              <input type="email" value={email} placeholder="admin@vavschool.com"
                onChange={e => { setEmail(e.target.value); setError(''); }}
                onFocus={() => setEmailFocused(true)} onBlur={() => setEmailFocused(false)}
                onKeyDown={e => e.key === 'Enter' && handleCredentials()}
                style={{ flex: 1, border: 'none', outline: 'none', fontSize: 15, padding: '15px 0', background: 'transparent', color: colors.text }} />
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: colors.textMuted, letterSpacing: '0.8px', marginBottom: 8 }}>
              PASSWORD
            </div>
            <div style={inputWrap(passFocused, !!error)}>
              <input type={showPass ? 'text' : 'password'} value={password} placeholder="Enter your password"
                onChange={e => { setPassword(e.target.value); setError(''); }}
                onFocus={() => setPassFocused(true)} onBlur={() => setPassFocused(false)}
                onKeyDown={e => e.key === 'Enter' && handleCredentials()}
                style={{ flex: 1, border: 'none', outline: 'none', fontSize: 15, padding: '15px 0', background: 'transparent', color: colors.text }} />
              <button onClick={() => setShowPass(s => !s)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
                <EyeIcon open={showPass} />
              </button>
            </div>
          </div>
          {error && (
            <div style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, borderRadius: 10, padding: '10px 14px', fontSize: 13, color: colors.danger }}>
              {error}
            </div>
          )}
        </div>
        <div style={{ flex: 1 }} />
      </div>
      <div style={{ padding: '12px 24px 40px', background: '#fff', flexShrink: 0 }}>
        <button onClick={handleCredentials} disabled={loading} style={{
          width: '100%', padding: '16px',
          background: loading ? colors.primaryLight : colors.primaryDark,
          color: '#fff', border: 'none', borderRadius: 14,
          fontSize: 16, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          {loading ? (
            <><div style={{ width: 16, height: 16, border: '2.5px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />Please wait...</>
          ) : 'Continue'}
        </button>
        <div style={{ textAlign: 'center', fontSize: 12, color: colors.textLight, marginTop: 12 }}>
          Managed by VNEW Trust
        </div>
      </div>
    </div>
  );

  return (
    <div style={baseContainer}>
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '56px 24px 0' }}>
          <button onClick={() => { setStep('credentials'); setOtp(['', '', '', '', '', '']); setOtpError(''); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 500, color: colors.text, padding: 0 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={colors.text} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
            </svg>
            Back
          </button>
        </div>
        <div style={{ padding: '28px 24px 0' }}>
          <div style={{ fontSize: 26, fontWeight: 900, color: colors.text }}>Verify OTP</div>
          <div style={{ fontSize: 14, color: colors.textMuted, marginTop: 6 }}>
            OTP sent to - Administrator
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'min(10px,2vw)', padding: '36px 24px 0' }} onPaste={handlePaste}>
          {[0,1,2].map(i => (
            <input key={i} ref={el => { otpRefs.current[i] = el; }} type="text" inputMode="numeric" maxLength={1}
              value={otp[i]} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKey(i, e)}
              style={{ width: 'min(58px,13vw)', height: 'min(62px,14vw)', textAlign: 'center', fontSize: 'min(24px,5.5vw)', fontWeight: 700,
                border: `2px solid ${otpError ? colors.dangerBorder : otp[i] ? colors.primary : colors.border}`,
                borderRadius: 14, background: '#fff', color: colors.text, outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box' }} />
          ))}
          <div style={{ fontSize: 'min(22px,5vw)', color: colors.textLight, fontWeight: 300, flexShrink: 0 }}>-</div>
          {[3,4,5].map(i => (
            <input key={i} ref={el => { otpRefs.current[i] = el; }} type="text" inputMode="numeric" maxLength={1}
              value={otp[i]} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKey(i, e)}
              style={{ width: 'min(58px,13vw)', height: 'min(62px,14vw)', textAlign: 'center', fontSize: 'min(24px,5.5vw)', fontWeight: 700,
                border: `2px solid ${otpError ? colors.dangerBorder : otp[i] ? colors.primary : colors.border}`,
                borderRadius: 14, background: '#fff', color: colors.text, outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box' }} />
          ))}
        </div>
        {otpError && (
          <div style={{ margin: '16px 24px 0', background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, borderRadius: 10, padding: '10px 14px', fontSize: 13, color: colors.danger, textAlign: 'center' }}>
            {otpError}
          </div>
        )}
        <div style={{ textAlign: 'right', padding: '16px 24px 0' }}>
          <button onClick={handleResend} disabled={resendCooldown > 0}
            style={{ background: 'none', border: 'none', cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: resendCooldown > 0 ? colors.textLight : colors.primary }}>
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
          </button>
        </div>
        <div style={{ flex: 1 }} />
      </div>
      <div style={{ padding: '12px 24px 40px', background: '#fff', flexShrink: 0 }}>
        <button onClick={handleVerify} disabled={otpLoading || otp.join('').length < 6} style={{
          width: '100%', padding: '16px',
          background: (otpLoading || otp.join('').length < 6) ? colors.primaryLight : colors.primaryDark,
          color: '#fff', border: 'none', borderRadius: 14, fontSize: 16, fontWeight: 700,
          cursor: (otpLoading || otp.join('').length < 6) ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          {otpLoading ? (
            <><div style={{ width: 16, height: 16, border: '2.5px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />Verifying...</>
          ) : 'Verify & Login'}
        </button>
        <div style={{ textAlign: 'center', fontSize: 12, color: colors.textLight, marginTop: 12 }}>Managed by VNEW Trust</div>
      </div>
    </div>
  );
};

export default LoginScreen;
